import React, { useState } from "react";
import { useNotifications } from "../context/NotificationContext";
import { useUserRole } from "../context/UserRoleContext";
import DashboardLayout from "../components/common/DashboardLayout";
import InviteResponseModal from "../components/common/InviteResponseModal";
import ShowAcceptDeclineModal from "../components/common/ShowAcceptDeclineModal";
import ShowResponseViewModal from "../components/common/ShowResponseViewModal";
import ConfirmModal from "../components/common/ConfirmModal";
import { useNotificationNavigation } from "../hooks/useNotificationNavigation";
import {
  isNotificationGroupActionRequired,
  notificationCountsForBadge,
} from "../utils/notificationKinds";

// Import useArtist - will only work if user is an artist (within ArtistProvider)
let useArtist = null;
try {
  const artistContext = require("../context/ArtistContext");
  useArtist = artistContext.useArtist;
} catch (e) {
  // Context file not found - shouldn't happen but handle gracefully
}

const Notifications = () => {
  const {
    groupedNotifications,
    notifications,
    loading,
    markAsRead,
    markGroupAsRead,
    deleteNotification,
    markAllAsRead,
    clearFiltered,
    fetchNotifications,
  } = useNotifications();
  const { userData } = useUserRole();

  let refreshShows = null;
  let refreshArtists = null;
  if (useArtist && userData?.role === "artist") {
    try {
      const artistContext = useArtist();
      refreshShows = artistContext?.refreshShows;
      refreshArtists = artistContext?.refreshArtists;
    } catch (e) {
      // Not within ArtistProvider - that's okay for non-artist users
    }
  }

  const {
    handleNotificationClick,
    inviteModal,
    showModal,
    barOwnerModal,
  } = useNotificationNavigation({ refreshShows, refreshArtists });

  const [filter, setFilter] = useState("all"); // all, unread, shows, activity
  const [clearConfirm, setClearConfirm] = useState({
    open: false,
    filter: null,
  });

  const { informationalUnreadCount } = notificationCountsForBadge(
    notifications,
    userData?.role,
  );

  const getNotificationCategory = (type) => {
    const showTypes = [
      "show_booked",
      "show_cancelled",
      "show_updated",
      "show_pending",
      "show_accepted",
      "show_declined",
    ];
    return showTypes.includes(type) ? "shows" : "activity";
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "show_booked":
        return "🎤";
      case "show_cancelled":
        return "❌";
      case "show_updated":
        return "📝";
      case "show_pending":
        return "⏳";
      case "show_accepted":
        return "✅";
      case "show_declined":
        return "❌";
      case "invite_received":
        return "📨";
      case "invite_accepted":
      case "join_request_approved":
        return "✅";
      case "invite_declined":
      case "join_request_rejected":
        return "⛔";
      case "artist_requested":
      case "artist_joined":
      case "artist_left_bar":
        return "🎵";
      case "admin_added":
        return "👤";
      case "admin_removed":
        return "👋";
      default:
        return "🔔";
    }
  };

  const renderNotificationMessage = (notification) => {
    const type = notification?.latestNotification?.type;
    const message =
      notification?.message ?? notification?.latestNotification?.message;
    if (type !== "artist_left_bar" || !message) {
      return (
        <p className="notification-card-message">{notification?.message}</p>
      );
    }
    const lines = message.split("\n").filter(Boolean);
    const intro = lines[0] || "";
    const showLines = lines.slice(1);
    return (
      <div className="notification-card-message">
        <p className="notification-artist-left-bar-intro">{intro}</p>
        {showLines.length > 0 && (
          <div className="notification-artist-left-bar-list">
            {showLines.map((line, i) => {
              const parts = line.split(" · ");
              const date = parts[0] || line;
              const time = parts[1] || null;
              return (
                <div key={i} className="notification-artist-left-bar-item">
                  <strong>{date}</strong>
                  {time && (
                    <span className="notification-artist-left-bar-time">
                      {time}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const notifDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    if (notifDate.getTime() === today.getTime()) {
      return `Today at ${date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })}`;
    } else if (notifDate.getTime() === yesterday.getTime()) {
      return `Yesterday at ${date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })}`;
    } else {
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    }
  };

  const showsNotifications = groupedNotifications.filter(
    (n) => getNotificationCategory(n.latestNotification.type) === "shows",
  );
  const activityNotifications = groupedNotifications.filter(
    (n) => getNotificationCategory(n.latestNotification.type) === "activity",
  );

  const filteredNotifications = (() => {
    if (filter === "unread") {
      return groupedNotifications.filter((n) => n.unreadCount > 0);
    }
    if (filter === "shows") return showsNotifications;
    if (filter === "activity") return activityNotifications;
    return groupedNotifications;
  })();

  const unreadCount = groupedNotifications.filter(
    (n) => n.unreadCount > 0,
  ).length;
  const showsCount = showsNotifications.length;
  const activityCount = activityNotifications.length;

  const handleClearAllClick = () => {
    setClearConfirm({ open: true, filter });
  };

  const handleClearConfirm = () => {
    if (clearConfirm.filter) {
      clearFiltered(clearConfirm.filter);
    }
    setClearConfirm({ open: false, filter: null });
  };

  const handleClearCancel = () => {
    setClearConfirm({ open: false, filter: null });
  };

  const clearConfirmLabel =
    {
      all: "all notifications",
      unread: "unread notifications",
      shows: "show notifications",
      activity: "activity notifications",
    }[clearConfirm.filter] || "these notifications";

  const clearButtonLabel =
    filter === "all"
      ? "Clear All"
      : filter === "unread"
      ? "Clear Unread"
      : filter === "shows"
      ? "Clear Shows"
      : "Clear Activity";

  return (
    <DashboardLayout>
      {inviteModal.isOpen && inviteModal.inviteId && (
        <InviteResponseModal
          isOpen={inviteModal.isOpen}
          onClose={inviteModal.onClose}
          inviteId={inviteModal.inviteId}
          notificationId={inviteModal.notificationId}
          onSuccess={inviteModal.onSuccess}
        />
      )}
      {showModal.isOpen && (showModal.showId || showModal.recurringGroupId) && (
        <ShowAcceptDeclineModal
          isOpen={showModal.isOpen}
          onClose={showModal.onClose}
          showId={showModal.showId}
          recurringGroupId={showModal.recurringGroupId}
          onSuccess={showModal.onSuccess}
        />
      )}
      {barOwnerModal.isOpen && barOwnerModal.recurringGroupId && (
        <ShowResponseViewModal
          isOpen={barOwnerModal.isOpen}
          onClose={barOwnerModal.onClose}
          recurringGroupId={barOwnerModal.recurringGroupId}
        />
      )}
      <ConfirmModal
        open={clearConfirm.open}
        title="Clear notifications?"
        message={`Clear ${clearConfirmLabel}? This cannot be undone.`}
        confirmLabel="Clear"
        cancelLabel="Cancel"
        onConfirm={handleClearConfirm}
        onCancel={handleClearCancel}
      />
      <div className="notifications-page">
        <div className="notifications-header">
          <h1>Notifications</h1>

          <div className="notifications-actions">
            {informationalUnreadCount > 0 && (
              <button className="btn btn-outline" onClick={markAllAsRead}>
                Mark all as read
              </button>
            )}
            {filteredNotifications.length > 0 && (
              <button
                className="btn btn-outline clear-all-btn"
                onClick={handleClearAllClick}
              >
                {clearButtonLabel}
              </button>
            )}
          </div>
        </div>

        <div className="notifications-filters">
          <button
            className={`filter-btn ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All ({groupedNotifications.length})
          </button>
          <button
            className={`filter-btn ${filter === "unread" ? "active" : ""}`}
            onClick={() => setFilter("unread")}
          >
            Unread ({unreadCount})
          </button>
          <button
            className={`filter-btn ${filter === "shows" ? "active" : ""}`}
            onClick={() => setFilter("shows")}
          >
            Shows ({showsCount})
          </button>
          <button
            className={`filter-btn ${filter === "activity" ? "active" : ""}`}
            onClick={() => setFilter("activity")}
          >
            Activity ({activityCount})
          </button>
        </div>

        <div className="notifications-content">
          {loading ? (
            <div className="notifications-loading">
              <div className="spinner"></div>
              <p>Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="no-notifications-page">
              <span className="no-notif-icon">🔕</span>
              <h2>
                No{" "}
                {filter === "unread"
                  ? "unread "
                  : filter === "shows"
                  ? "show "
                  : filter === "activity"
                  ? "activity "
                  : ""}
                notifications
              </h2>
              <p>
                {filter === "unread"
                  ? "You're all caught up! All notifications have been read."
                  : filter === "shows"
                  ? "No show-related notifications."
                  : filter === "activity"
                  ? "No activity notifications (invites, joins, etc.)."
                  : "You don't have any notifications yet. We'll notify you when something important happens."}
              </p>
            </div>
          ) : (
            <div className="notifications-list-page">
              {filteredNotifications.map((notification) => {
                const actionable = isNotificationGroupActionRequired(
                  notification,
                  userData?.role,
                );
                return (
                <div
                  key={notification.groupId}
                  className={`notification-card ${
                    notification.unreadCount > 0 ? "unread" : ""
                  } ${actionable ? "notification-card--action-required" : ""}`}
                >
                  <div className="notification-card-icon">
                    {getNotificationIcon(notification.latestNotification.type)}
                  </div>

                  <div
                    className="notification-card-content"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-card-header">
                      <h3 className="notification-card-title">
                        {notification.title}
                      </h3>
                      <span className="notification-card-time">
                        {formatDate(notification.latestCreatedAt)}
                      </span>
                    </div>

                    {actionable && (
                      <span className="notification-action-required-badge">
                        Action required
                      </span>
                    )}

                    {renderNotificationMessage(notification)}

                    {notification.latestNotification.sender_name && (
                      <p className="notification-card-sender">
                        From: {notification.latestNotification.sender_name}
                      </p>
                    )}
                  </div>

                  {!actionable && (
                  <div className="notification-card-actions">
                    {notification.unreadCount > 0 && (
                      <button
                        className="notification-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            notification.isMessageGroup &&
                            notification.notifications.length > 1
                          ) {
                            markGroupAsRead(notification);
                          } else {
                            markAsRead(
                              notification.latestNotification.notification_id,
                            );
                          }
                        }}
                        title="Mark as read"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="notification-action-btn delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        notification.notifications.forEach((n) =>
                          deleteNotification(n.notification_id),
                        );
                      }}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Notifications;
